import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom'
import {
  fetchGalleryInfo,
  fetchGallery,
  fetchGalleryLandingInfo,
  getCoverPublicUrl,
  uploadCollection,
  deleteItem,
  deleteCollection,
  resolveSignedUrls,
  resolveSignedUrlsGeo,
  STORAGE_BUCKET,
  isAdmin,
} from '../lib/api'
import type { SignedUrlVariants } from '../lib/api'
import { getGeoSession, setGeoSession, clearGeoSession, revokeGeoSession, verifyGeo } from '../lib/geoSession'
import { resizeImage } from '../lib/imageResize'
import type { ExifData } from '../lib/exifExtract'
import { supabase } from '../lib/supabase'
import type { Gallery as GalleryType, Collection, Item } from '../lib/types'
import AuthImage from '../components/AuthImage'
import CollectionGrid from '../components/gallery/CollectionGrid'
import PhotoViewer from '../components/viewer/PhotoViewer'
import UploadModal from '../components/upload/UploadModal'
import GalleryCreateForm from '../components/GalleryCreateForm'
import GalleryEditForm from '../components/GalleryEditForm'
import CreateGalleryModal from '../components/CreateGalleryModal'
import QrFinderEyeLogo from '../components/QrFinderEyeLogo'
import { metricCount, metricDistribution } from '../lib/metrics'
import { syncSentryUser, syncSentryGalleryContext, type SentryRole } from '../lib/sentry'

const GEO_RECHECK_INTERVAL_MS = 5 * 60 * 1000
// Minimum time (ms) to show the verifying/unlocking state so transitions don't flicker
const MIN_TRANSITION_MS = 200

function withMinDelay<T>(promise: PromiseLike<T>, ms: number): Promise<T> {
  return Promise.all([Promise.resolve(promise), new Promise<void>((r) => setTimeout(r, ms))]).then(([result]) => result)
}

type Phase = 'loading' | 'unclaimed' | 'locked' | 'open'

interface GeoItemRow {
  id: string
  position: number
  fileType: string
  thumbnailUrl: string
  fullUrl: string
  storagePath: string | null
  createdAt: string | null
  camera: string | null
  gpsLat: number | null
  gpsLng: number | null
}

interface GeoCollectionRow {
  id: string
  username: string
  title: string | null
  createdAt: string
  date: string | null
  ownerId: string | null
  items: GeoItemRow[]
}

interface GeoGalleryRow {
  id: string
  shortCode: string
  name: string
  landingImageUrl: string
  ownerId: string | null
  collections: GeoCollectionRow[]
}

// Maps the JSONB response from fetch_gallery_geo RPC to our Gallery type
function mapGeoGallery(data: GeoGalleryRow): GalleryType {
  return {
    id: data.id,
    shortCode: data.shortCode ?? '',
    name: data.name ?? '',
    landingImageUrl: data.landingImageUrl ?? '',
    ownerId: data.ownerId ?? null,
    collections: (data.collections ?? []).map((c: GeoCollectionRow): Collection => ({
      id: c.id,
      username: c.username,
      title: c.title ?? null,
      createdAt: c.createdAt,
      date: c.date ?? null,
      ownerId: c.ownerId ?? null,
      items: (c.items ?? []).map((i: GeoItemRow): Item => ({
        id: i.id,
        position: i.position ?? 0,
        fileType: i.fileType === 'video' ? 'video' : 'photo',
        thumbnailUrl: i.thumbnailUrl ?? '',
        fullUrl: i.fullUrl ?? '',
        storagePath: i.storagePath ?? undefined,
        createdAt: i.createdAt ?? null,
        metadata: (i.camera || i.gpsLat) ? {
          ...(i.camera ? { camera: i.camera } : {}),
          ...(i.gpsLat != null && i.gpsLng != null ? { gps: { lat: i.gpsLat, lng: i.gpsLng } } : {}),
        } : undefined,
      })),
    })),
  }
}

// ─── Confirm-to-claim (unclaimed gallery, logged-in owner) ────────────────────

function GalleryClaimConfirm({
  ownerEmail,
  onContinue,
  onAbort,
}: {
  ownerEmail: string
  onContinue: () => void
  onAbort: () => void
}) {
  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-gray-900 rounded-2xl p-8 flex flex-col gap-6 text-center">
        <div className="flex flex-col gap-2">
          <h1 className="text-white text-2xl font-bold">Sett opp galleri</h1>
          <p className="text-gray-400 text-sm leading-relaxed">
            Dette galleriet er ikke satt opp enda. Vil du sette det opp som{' '}
            <span className="text-gray-200">{ownerEmail}</span>?
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={onContinue}
            className="
              w-full py-3 rounded-xl
              bg-blue-500 hover:bg-blue-600
              text-white font-semibold
              transition-colors duration-200
              flex items-center justify-center gap-2
            "
          >
            Ja, sett opp galleri
          </button>

          <button
            onClick={onAbort}
            className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
          >
            Nei, logg inn med en annen bruker
          </button>
        </div>
      </div>
    </div>
  )
}

function LoginPrompt() {
  const location = useLocation()
  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-gray-900 rounded-2xl p-8 flex flex-col gap-6 text-center">
        <h1 className="text-white text-2xl font-bold">Sett opp galleri</h1>
        <p className="text-gray-400 text-sm leading-relaxed">
          Du må logge inn for å sette opp dette galleriet.
        </p>
        <Link
          to={`/login?next=${encodeURIComponent(location.pathname)}`}
          className="block w-full py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold transition-colors duration-200 text-center"
        >
          Logg inn
        </Link>
      </div>
    </div>
  )
}

// ─── Top-right title bar menu ─────────────────────────────────────────────────

function TitleBarMenu({
  authenticated,
  isOwner,
  isEmpty,
  editMode,
  onEditToggle,
  onEditGallery,
  onLogout,
  loginHref,
  iconClass,
}: {
  authenticated: boolean
  isOwner: boolean
  isEmpty: boolean
  editMode: boolean
  onEditToggle: () => void
  onEditGallery: () => void
  onLogout: () => void
  loginHref: string
  iconClass: string
}) {
  const [open, setOpen] = useState(false)

  const menuItemClass = "flex items-center gap-3 w-full px-4 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/10 transition-colors duration-150 text-left"

  const hasEdit = isOwner && !isEmpty
  const hasEditGallery = isOwner
  const itemCount = (hasEdit ? 1 : 0) + (hasEditGallery ? 1 : 0) + (authenticated ? 2 : 1)

  // If there is only one menu item, render it inline instead of behind a dropdown
  if (itemCount === 1) {
    // Only case: not authenticated and no edit action → show "Logg inn" directly
    return (
      <Link
        to={loginHref}
        className={iconClass}
        aria-label="Logg inn"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l3 3m0 0l-3 3m3-3H3.75" />
        </svg>
      </Link>
    )
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={iconClass}
        aria-label="Meny"
        aria-expanded={open}
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
        </svg>
      </button>

      {open && (
        <>
          {/* Backdrop to close on outside click */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 min-w-[160px] bg-gray-900/95 backdrop-blur-sm rounded-xl shadow-xl overflow-hidden border border-white/10">
            {authenticated && (
              <Link
                to="/manage"
                onClick={() => setOpen(false)}
                className={menuItemClass}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 flex-none">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                </svg>
                Mine gallerier
              </Link>
            )}
            {hasEditGallery && (
              <button
                onClick={() => { onEditGallery(); setOpen(false) }}
                className={menuItemClass}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 flex-none">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 8.25H7.5a2.25 2.25 0 00-2.25 2.25v9a2.25 2.25 0 002.25 2.25h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25H15M12 1.5v12m0 0l-3-3m3 3l3-3" />
                </svg>
                Rediger galleri
              </button>
            )}
            {hasEdit && (
              <button
                onClick={() => { onEditToggle(); setOpen(false) }}
                className={menuItemClass}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 flex-none">
                  {editMode
                    ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    : <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
                  }
                </svg>
                {editMode ? 'Ferdig' : 'Slett bilder'}
              </button>
            )}
            <div className={authenticated || hasEdit ? 'border-t border-white/10' : ''}>
              {authenticated ? (
                <button
                  onClick={() => { onLogout(); setOpen(false) }}
                  className={menuItemClass}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 flex-none">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                  </svg>
                  Logg ut
                </button>
              ) : (
                <Link
                  to={loginHref}
                  onClick={() => setOpen(false)}
                  className={menuItemClass}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 flex-none">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l3 3m0 0l-3 3m3-3H3.75" />
                  </svg>
                  Logg inn
                </Link>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function Gallery() {
  const { shortCode } = useParams<{ shortCode: string }>()
  const location = useLocation()
  const navigate = useNavigate()

  // Redirect uppercase short codes (from QR codes) to their canonical lowercase form
  useEffect(() => {
    if (!shortCode) return
    const lower = shortCode.toLowerCase()
    if (lower !== shortCode) {
      navigate(location.pathname.replace(shortCode, lower) + location.search + location.hash, { replace: true })
    }
  }, [shortCode, location, navigate])

  const lowerShortCode = shortCode?.toLowerCase()

  // Read localStorage cache synchronously so the landing screen can render
  // immediately on refresh — no useEffect delay.
  const initialCache = (() => {
    if (!lowerShortCode) return null
    try {
      const raw = localStorage.getItem(`galleryLanding:${lowerShortCode}`)
      if (!raw) return null
      const cached = JSON.parse(raw)
      if (Date.now() - cached.cachedAt > 55 * 60 * 1000) return null
      return cached.data as {
        id: string
        name: string
        coverUrl: string | null
        coverBlur: string | null
        claimed: boolean
        ownerId: string | null
        locationLat: number | null
        locationLng: number | null
      }
    } catch {
      return null
    }
  })()

  // ── Resolved UUID (looked up from shortCode on mount) ──
  const [galleryId, setGalleryId] = useState<string | null | undefined>(
    initialCache?.id ?? undefined
  )
  // undefined = not yet resolved, null = short code not found, string = resolved UUID

  // ── Phase ──
  const [phase, setPhase] = useState<Phase>(
    initialCache ? (initialCache.claimed ? 'locked' : 'unclaimed') : 'loading'
  )

  // ── Landing / lock screen state ──
  const [galleryName, setGalleryName] = useState<string>(initialCache?.name ?? '')
  const [coverSrc, setCoverSrc] = useState<string>(initialCache?.coverUrl ?? '')
  const [coverBlur, setCoverBlur] = useState<string>(initialCache?.coverBlur ?? '')
  const [coverReady, setCoverReady] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [isUnlocking, setIsUnlocking] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [geoError, setGeoError] = useState<string | null>(null)
  const [loggedInEmail, setLoggedInEmail] = useState<string | null>(null)
  const [authChecked, setAuthChecked] = useState(false)

  // ── Gallery (open) state ──
  const [gallery, setGallery] = useState<GalleryType | null>(null)
  const [galleryLoading, setGalleryLoading] = useState(false)
  const [galleryError, setGalleryError] = useState<string | null>(null)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [viewerStartIndex, setViewerStartIndex] = useState(0)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)

  const [editMode, setEditMode] = useState(false)
  const [deletingItemIds, setDeletingItemIds] = useState<Set<string>>(new Set())
  const [geoToken, setGeoToken] = useState<string | null>(null)
  const [isOwner, setIsOwner] = useState(false)
  // Refs so the load effect can read current values without re-running when they change.
  // setIsOwner inside the effect would otherwise re-trigger the effect via the dep array.
  const isOwnerRef = useRef(false)
  const isOwnerlessRef = useRef(false)
  const ownerIdRef = useRef<string | null>(null)
  const userRef = useRef<{ id: string; email: string } | null>(null)
  const authCheckedRef = useRef(false)
  const authInitiatedRef = useRef(false)
  const [signedUrls, setSignedUrls] = useState<Map<string, SignedUrlVariants>>(new Map())

  const [scrollAfterUpload, setScrollAfterUpload] = useState(false)
  const [authenticated, setAuthenticated] = useState<boolean | null>(null)
  const [claimForm, setClaimForm] = useState(false)
  const [editGalleryOpen, setEditGalleryOpen] = useState(false)

  // ── Auth check (for unclaimed gallery claim flow + login/logout button) ──
  useEffect(() => {
    if (authInitiatedRef.current) return
    authInitiatedRef.current = true
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        userRef.current = { id: user.id, email: user.email ?? '' }
        if (user.email) setLoggedInEmail(user.email)
      }
      setAuthenticated(!!user)
      setAuthChecked(true)
      authCheckedRef.current = true
    })
  }, [])

  const handleLogout = async () => {
    await supabase.auth.signOut()
    // Stay on the gallery as an unauthenticated viewer — gallery routes are public
    setAuthenticated(false)
    setLoggedInEmail(null)
    setIsOwner(false); isOwnerRef.current = false
    isOwnerlessRef.current = false
    userRef.current = null
    // Invalidate any cached geo session so it cannot be replayed after logout
    const existingSession = getGeoSession(galleryId ?? '')
    if (existingSession) {
      clearGeoSession()
      revokeGeoSession(existingSession.token) // fire-and-forget DB revocation
    }
    setGeoToken(null)
    setGallery(null)
    setPhase('locked')
  }

  // ── Resolve short code → gallery info in one round-trip ──
  // Normalize to lowercase — QR codes encode as uppercase (e.g. ERDJT9) but all
  // stored short codes are lowercase, so /ERDJT9 and /erdjt9 must resolve identically.
  const shortCodeFetchedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!shortCode) return
    // Prevent duplicate fetches when the same short code is already in-flight
    const lower = shortCode.toLowerCase()
    if (shortCodeFetchedRef.current === lower) return
    shortCodeFetchedRef.current = lower

    const CACHE_TTL_MS = 55 * 60 * 1000 // 55 min

    type LandingInfo = {
      id: string
      name: string
      coverUrl: string | null
      coverBlur: string | null
      claimed: boolean
      ownerId: string | null
      locationLat: number | null
      locationLng: number | null
    }

    function loadFromCache(): LandingInfo | null {
      try {
        const raw = localStorage.getItem(`galleryLanding:${lower}`)
        if (!raw) return null
        const cached = JSON.parse(raw)
        if (Date.now() - cached.cachedAt > CACHE_TTL_MS) return null
        return cached.data
      } catch {
        return null
      }
    }

    function saveToCache(data: LandingInfo) {
      try {
        localStorage.setItem(
          `galleryLanding:${lower}`,
          JSON.stringify({ cachedAt: Date.now(), data })
        )
      } catch {
        // localStorage quota exceeded — silently skip caching
      }
    }

    function applyInfo(info: LandingInfo) {
      setGalleryId(info.id)
      setGalleryName(info.name)
      ownerIdRef.current = info.ownerId
      setCoverBlur(info.coverBlur ?? '')
      if (info.coverUrl) {
        setCoverSrc(info.coverUrl)
      } else {
        setCoverReady(true)
      }
      if (!info.claimed) {
        setPhase('unclaimed')
        return
      }

      // Ownerless galleries (sample/demo content) are public — no geo auth required
      if (!info.ownerId) {
        isOwnerlessRef.current = true
      }

      // Seed geo token from localStorage so a valid session survives refresh
      const existing = getGeoSession(info.id)
      if (existing) setGeoToken(existing.token)

      // Show the landing screen immediately — ownership check runs separately
      // when auth resolves, via initializeGallery.
      setPhase('locked')
    }

    const cached = loadFromCache()
    if (cached) {
      // Use stale cache immediately for instant paint; refresh in background
      applyInfo(cached)
      fetchGalleryLandingInfo(lower).then((fresh) => {
        if (fresh) {
          saveToCache(fresh)
          // Public URLs are stable — it's safe to update coverSrc if it changed
          if (fresh.coverUrl && fresh.coverUrl !== cached.coverUrl) {
            setCoverSrc(fresh.coverUrl)
          }
        }
      })
      return
    }

    fetchGalleryLandingInfo(lower).then((info) => {
      if (!info) {
        setGalleryId(null)
        return
      }
      saveToCache(info)
      applyInfo(info)
    })
  }, [shortCode])

  // ── Ownership check — called on-demand from handleUnlock so it never races
  // with the initial auth effect. By the time the user clicks unlock, auth
  // has already resolved (button is disabled until authChecked is true).
  const initializeGallery = useCallback(async () => {
    if (!galleryId) return
    setFetchError(null)
    try {
      // Detect owner / collaborator / admin status
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        userRef.current = { id: user.id, email: user.email ?? '' }
      }
      let owner = false
      if (user) {
        const { data: ownerRow } = await supabase
          .from('owners')
          .select('id')
          .eq('auth_user_id', user.id)
          .single()

        if (ownerRow) {
          const ownerId = ownerIdRef.current
          if (ownerId && ownerRow.id === ownerId) {
            owner = true
          } else {
            // Check if the user is a collaborator on this gallery
            const { data: collabRow } = await supabase
              .from('gallery_collaborators')
              .select('gallery_id')
              .eq('gallery_id', galleryId)
              .eq('linked_owner_id', ownerRow.id)
              .maybeSingle()
            owner = !!collabRow
          }
        }

        // Admins bypass geo auth for any gallery
        if (!owner) {
          owner = await isAdmin()
        }
      }
      if (owner) { setIsOwner(true); isOwnerRef.current = true }
    } catch (err) {
      setFetchError((err as Error).message)
    }
  }, [galleryId])

  // ── Load gallery data when phase transitions to 'open' ──
  useEffect(() => {
    if (phase !== 'open' || !galleryId) return

    setGalleryLoading(true)
    setGalleryError(null)
    ;(async () => {
      try {
        let g: GalleryType

        if (isOwnerRef.current) {
          // Owners bypass geo auth — fetch directly via RLS-backed query
          g = await withMinDelay(fetchGallery(galleryId), MIN_TRANSITION_MS)
        } else if (isOwnerlessRef.current) {
          // Ownerless (sample) galleries are public — fetch_gallery_geo skips token
          // validation for galleries with no owner_id; pass nil UUID as placeholder
          const { data, error } = await withMinDelay(
            supabase.rpc('fetch_gallery_geo', {
              p_gallery_id: galleryId,
              p_token: '00000000-0000-0000-0000-000000000000',
            }),
            MIN_TRANSITION_MS,
          )
          if (error) throw new Error(error.message)
          if (!data) throw new Error('Gallery not found')
          g = mapGeoGallery(data)
        } else {
          if (!geoToken) {
            setPhase('locked')
            return
          }
          const { data, error } = await withMinDelay(
            supabase.rpc('fetch_gallery_geo', { p_gallery_id: galleryId, p_token: geoToken }),
            MIN_TRANSITION_MS,
          )
          if (error) throw new Error(error.message)
          if (!data) throw new Error('Gallery not found')
          g = mapGeoGallery(data)

          // Check ownership / collaborator status for geo path as well
          const { data: { user } } = await supabase.auth.getUser()
          if (user) {
            userRef.current = { id: user.id, email: user.email ?? '' }
            const { data: ownerData } = await supabase
              .from('owners')
              .select('id')
              .eq('auth_user_id', user.id)
              .single()
            if (ownerData) {
              let ownerViaGeo = false
              if (g.ownerId && ownerData.id === g.ownerId) {
                ownerViaGeo = true
              } else {
                const { data: collabData } = await supabase
                  .from('gallery_collaborators')
                  .select('gallery_id')
                  .eq('gallery_id', galleryId)
                  .eq('linked_owner_id', ownerData.id)
                  .maybeSingle()
                ownerViaGeo = !!collabData
              }
              if (ownerViaGeo) { setIsOwner(true); isOwnerRef.current = true }
            }
          }
        }

        const allItems = g.collections.flatMap((c) => c.items)
        if (allItems.some((i) => i.storagePath)) {
          try {
            let urls: Map<string, SignedUrlVariants>
            if (isOwnerRef.current) {
              urls = await resolveSignedUrls(allItems)
            } else if (geoToken) {
              urls = await resolveSignedUrlsGeo(allItems, galleryId, geoToken)
            } else {
              urls = new Map()
            }
            setSignedUrls(urls)
          } catch (urlErr) {
            // URL signing failed (edge function cold-start, network blip, expired token).
            // Show the gallery anyway; images will stay as skeletons. The user can
            // retry by scrolling away and back, or refreshing.
            console.error('Failed to resolve signed URLs:', urlErr)
          }
        }

        setGallery(g)
        setIsUnlocking(false)
        metricCount('gallery.opened', 1)
      } catch (err) {
        setIsUnlocking(false)
        setGalleryError((err as Error).message)
      } finally {
        setGalleryLoading(false)
      }
    })()
  // isOwnerRef/isOwnerlessRef are refs — intentionally omitted from deps to
  // prevent the effect re-running when ownership is confirmed mid-load.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, galleryId, geoToken])

  // ── Keep Sentry user context in sync with the geo session ──
  // syncSentryUser resolves a real logged-in user first, so owners are keyed by
  // email while anonymous geo visitors are keyed by their session token.
  useEffect(() => {
    syncSentryUser()
  }, [geoToken])

  // ── Stamp the current gallery + actor role onto the Sentry scope ──
  // This tags gallery_id/short_code and a role (owner/admin/user/geo/anon) so
  // issues can be filtered per gallery and per access type.
  useEffect(() => {
    const short = shortCode?.toLowerCase() ?? ''
    if (!galleryId) {
      syncSentryGalleryContext({ galleryId: null, galleryName: '', role: 'anon' })
      return
    }
    ;(async () => {
      const admin = await isAdmin()
      let role: SentryRole
      if (isOwner) role = 'owner'
      else if (admin) role = 'admin'
      else if (geoToken) role = 'geo'
      else if (authenticated) role = 'user'
      else role = 'anon'
      syncSentryGalleryContext({ galleryId, galleryName, shortCode: short, isOwner, isAdmin: admin, role })
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryId, galleryName, isOwner, geoToken])

  // ── Background geo re-check every 5 minutes ──
  useEffect(() => {
    if (phase !== 'open' || !galleryId || isOwnerRef.current || isOwnerlessRef.current) return

    const recheck = async () => {
      const session = getGeoSession(galleryId)
      if (!session) {
        setPhase('locked')
        return
      }

      const result = await verifyGeo(galleryId)
      if (result.ok) {
        setGeoSession(galleryId, result.token, result.expiresAt)
      } else {
        clearGeoSession()
        setPhase('locked')
      }
    }

    const interval = setInterval(recheck, GEO_RECHECK_INTERVAL_MS)
    return () => clearInterval(interval)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, galleryId])


  // ── Scroll to new upload (collections are newest-first, so always scroll to top) ──
  useEffect(() => {
    if (!scrollAfterUpload) return
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setScrollAfterUpload(false)
  }, [scrollAfterUpload])

  // ── Geo unlock ──
  const handleUnlock = async () => {
    if (!galleryId) return

    setIsVerifying(true)
    setGeoError(null)

    // If ownership hasn't been determined yet (race on initial load), resolve it now
    if (!isOwnerRef.current && !isOwnerlessRef.current) {
      await initializeGallery()
    }

    if (isOwnerRef.current || isOwnerlessRef.current) {
      setIsVerifying(false)
      setIsUnlocking(true)
      setGalleryLoading(true)
      setPhase('open')
      return
    }

    const existingSession = getGeoSession(galleryId)
    if (existingSession) {
      setGeoToken(existingSession.token)
      setIsVerifying(false)
      setIsUnlocking(true)
      setGalleryLoading(true)
      setPhase('open')
      return
    }

    const result = await withMinDelay(verifyGeo(galleryId), MIN_TRANSITION_MS)

    setIsVerifying(false)

    if (result.ok) {
      setGeoSession(galleryId, result.token, result.expiresAt)
      setGeoToken(result.token)
      setIsUnlocking(true)
      setGalleryLoading(true)
      setPhase('open')
      return
    }

    switch (result.reason) {
      case 'outside_range':
        setGeoError('Du ække h3r')
        break
      case 'location_denied':
        setGeoError('Stedstilgang kreves for å åpne galleriet\nAktiver stedstjenester i nettleseren og prøv igjen')
        break
      case 'gallery_not_found':
        setGeoError('Galleriet ble ikke funnet')
        break
      default:
        setGeoError('Noe gikk galt. Prøv igjen.')
    }
  }

  // ── Derived gallery data ──
  // Collections are always displayed newest-first
  const sortedGallery = useMemo(() => {
    if (!gallery) return null
    const sorted = [...gallery.collections].sort((a, b) => {
      const dateA = new Date(a.date ?? a.createdAt).getTime()
      const dateB = new Date(b.date ?? b.createdAt).getTime()
      return dateB - dateA
    })
    return { ...gallery, collections: sorted }
  }, [gallery])

  const allItems = useMemo(() => {
    if (!sortedGallery) return []
    return sortedGallery.collections.flatMap((c) => {
      const sortedItems = [...c.items].sort((a, b) => a.position - b.position)
      return sortedItems.map((item) => ({ ...item, username: c.username, collectionTitle: c.title }))
    })
  }, [sortedGallery])

  // ── Handlers ──
  const handleDeleteItem = async (itemId: string, collectionId: string, storagePath?: string) => {
    setDeletingItemIds((prev) => new Set(prev).add(itemId))
    try {
      const { collectionDeleted } = await deleteItem(itemId, collectionId, storagePath)
      setGallery((prev) => {
        if (!prev) return prev
        if (collectionDeleted) {
          return { ...prev, collections: prev.collections.filter((c) => c.id !== collectionId) }
        }
        return {
          ...prev,
          collections: prev.collections.map((c) =>
            c.id === collectionId
              ? { ...c, items: c.items.filter((i) => i.id !== itemId) }
              : c
          ),
        }
      })
    } finally {
      setDeletingItemIds((prev) => {
        const next = new Set(prev)
        next.delete(itemId)
        return next
      })
    }
  }

  const handleDeleteCollection = async (collectionId: string) => {
    const collection = gallery?.collections.find((c) => c.id === collectionId)
    if (!collection) return
    const storagePaths = collection.items
      .map((i) => i.storagePath)
      .filter((p): p is string => !!p)
    await deleteCollection(collectionId, storagePaths)
    setGallery((prev) =>
      prev ? { ...prev, collections: prev.collections.filter((c) => c.id !== collectionId) } : prev
    )
  }

  const handleItemClick = (itemId: string) => {
    const index = allItems.findIndex((item) => item.id === itemId)
    if (index !== -1) {
      metricCount('photo.viewed', 1)
      setViewerStartIndex(index)
      setViewerOpen(true)
    }
  }

  const handleUpload = async (files: { file: File; exif: ExifData }[], username: string, title: string, collectionDate?: Date) => {
    if (!galleryId || !gallery) return

    if (isOwner) {
      const newCollection = await uploadCollection(galleryId, username, files, { collectionDate, title: title || undefined })
      setGallery((prev) =>
        prev ? { ...prev, collections: [newCollection, ...prev.collections] } : prev
      )
      resolveSignedUrls(newCollection.items).then((newUrls) => {
        setSignedUrls((prev) => new Map([...prev, ...newUrls]))
      })
      setScrollAfterUpload(true)
      return
    }

    if (!geoToken) {
      setPhase('locked')
      return
    }

    const collectionId = crypto.randomUUID()
    const geoUploadStart = performance.now()

    const itemPayloads = await Promise.all(
      files.map(async ({ file, exif }, index) => {
        const resized = await resizeImage(file)
        const ext = resized.name.split('.').pop() ?? 'jpg'
        const storagePath = `${galleryId}/${collectionId}/${crypto.randomUUID()}.${ext}`

        const { error: uploadError } = await supabase.storage
          .from(STORAGE_BUCKET)
          .upload(storagePath, resized, { upsert: false })
        if (uploadError) throw new Error(uploadError.message)

        return {
          file_type: file.type.startsWith('video/') ? 'video' : 'photo',
          storage_path: storagePath,
          created_at: exif.createdAt ?? '',
          position: index,
          camera: exif.camera ?? '',
          gps_lat: exif.gps?.lat?.toString() ?? '',
          gps_lng: exif.gps?.lng?.toString() ?? '',
        }
      })
    )

    const { data, error: rpcError } = await supabase.rpc('upload_collection_geo', {
      p_token: geoToken,
      p_gallery_id: galleryId,
      p_collection_id: collectionId,
      p_username: username,
      p_created_at: null,
      p_date: collectionDate?.toISOString() ?? null,
      p_items: itemPayloads,
      p_title: title || null,
    })
    if (rpcError) throw new Error(rpcError.message)

    metricCount('collection.uploaded', 1, { count: files.length, via: 'geo' })
    metricDistribution(
      'collection.upload.duration_ms',
      Math.round(performance.now() - geoUploadStart),
      'millisecond',
      { count: files.length, via: 'geo' },
    )

    const newCollection = {
      id: collectionId,
      username,
      title: (data.title as string | null) ?? null,
      createdAt: data.createdAt as string,
      date: (data.date as string | null) ?? null,
      ownerId: null, // geo uploads are always anon
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      items: (data.items as any[]).map((item) => ({
        id: item.id as string,
        position: item.position as number,
        fileType: item.fileType as 'photo' | 'video',
        thumbnailUrl: '',
        fullUrl: '',
        storagePath: item.storagePath as string,
        createdAt: (item.createdAt as string | null) ?? null,
        metadata: (item.camera || item.gpsLat) ? {
          ...(item.camera ? { camera: item.camera as string } : {}),
          ...(item.gpsLat != null ? { gps: { lat: item.gpsLat as number, lng: item.gpsLng as number } } : {}),
        } : undefined,
      })),
    }

    setGallery((prev) =>
      prev ? { ...prev, collections: [newCollection, ...prev.collections] } : prev
    )
    if (geoToken) {
      resolveSignedUrlsGeo(newCollection.items, galleryId, geoToken).then((newUrls) => {
        setSignedUrls((prev) => new Map([...prev, ...newUrls]))
      })
    }
    setScrollAfterUpload(true)
  }

  // ── Render: short code not found ──
  if (galleryId === null) {
    if (!loggedInEmail) return <LoginPrompt />
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="rounded-lg bg-gray-900 border border-gray-800 p-6 text-gray-400 max-w-sm mx-4 text-center">
          Galleriet ble ikke funnet
        </div>
      </div>
    )
  }

  // ── Render: error ──
  if (fetchError) {
    if (!loggedInEmail) return <LoginPrompt />
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="rounded-lg bg-red-900 border border-red-700 p-6 text-red-200 max-w-sm mx-4">
          Kunne ikke laste galleriet: {fetchError}
        </div>
      </div>
    )
  }

  // ── Render: initial loading / short code resolution ──
  // Auth check no longer blocks rendering — the landing screen is shown
  // immediately when cached, and the lock button is disabled until auth resolves.
  if (phase === 'loading' || galleryId === undefined) {
    return (
      <div className="fixed inset-0 z-50 bg-gray-950 flex flex-col items-center justify-center h-full gap-6">
        <QrFinderEyeLogo size={40} className="text-white" />
      </div>
    )
  }

  // ── Render: unclaimed ──
  if (phase === 'unclaimed') {
    const onClaimDone = () => {
      setClaimForm(false)
      setPhase('loading')
      fetchGalleryInfo(galleryId!)
        .then(({ name, landingImageUrl: imageUrl, landingImageBlur: blur, claimed: isClaimed }) => {
          setGalleryName(name)
          setCoverBlur(blur ?? '')
          if (imageUrl) {
            setCoverSrc(getCoverPublicUrl(imageUrl))
          }
          setPhase(isClaimed ? 'locked' : 'unclaimed')
        })
        .catch(() => setPhase('locked'))
    }

    if (!loggedInEmail) {
      return (
        <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
          <div className="w-full max-w-sm bg-gray-900 rounded-2xl p-8 flex flex-col gap-6 text-center">
            <h1 className="text-white text-2xl font-bold">Sett opp galleri</h1>
            <p className="text-gray-400 text-sm leading-relaxed">
              Du må logge inn for å sette opp dette galleriet.
            </p>
            <Link
              to={`/login?next=${encodeURIComponent(location.pathname)}`}
              className="block w-full py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold transition-colors duration-200 text-center"
            >
              Logg inn
            </Link>
          </div>
        </div>
      )
    }

    if (loggedInEmail && !claimForm) {
      return <GalleryClaimConfirm ownerEmail={loggedInEmail} onContinue={() => setClaimForm(true)} onAbort={handleLogout} />
    }

    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="w-full max-w-lg bg-gray-900 rounded-2xl p-8 flex flex-col gap-6">
          <GalleryCreateForm
            galleryId={galleryId!}
            initialEmail={loggedInEmail ?? undefined}
            onDone={onClaimDone}
            onAbort={loggedInEmail ? () => setClaimForm(false) : undefined}
          />
        </div>
      </div>
    )
  }

  // ── Render ──
  const isEmpty = sortedGallery ? sortedGallery.collections.length === 0 : false
  const showSplash = phase === 'open' && !isOwner && (galleryLoading || !sortedGallery)
  const iconClass = "flex-none text-white/40 hover:text-white/80 transition-colors duration-200 focus:outline-none p-2"

  return (
    <div className="min-h-dvh bg-gray-950">

      {/* Open gallery content — underneath the lock overlay while loading */}
      {phase === 'open' && (
        <>
          {galleryError ? (
            <div className="min-h-screen bg-gray-950 p-4 flex items-center justify-center">
              <div className="rounded-lg bg-red-900 border border-red-700 p-6 text-red-200 max-w-sm">
                Kunne ikke laste galleriet: {galleryError}
              </div>
            </div>
          ) : sortedGallery ? (
            <>

              {isEmpty ? (
                <div className="relative min-h-dvh flex items-center justify-center">
                  <div className="absolute top-0 right-0 z-10 pt-4 pr-4 flex items-center">
                    {authenticated !== null && (
                      <TitleBarMenu
                        authenticated={authenticated}
                        isOwner={isOwner}
                        isEmpty={isEmpty}
                        editMode={editMode}
                        onEditToggle={() => setEditMode((prev) => !prev)}
                        onEditGallery={() => setEditGalleryOpen(true)}
                        onLogout={handleLogout}
                        loginHref={`/login?next=${encodeURIComponent(location.pathname)}`}
                        iconClass={iconClass}
                      />
                    )}
                  </div>
                  <div className="flex flex-col items-center gap-6">
                    <h1 className="text-white/60 hover:text-white/90 transition-colors duration-200 text-4xl font-thin tracking-tight select-none leading-none text-center drop-shadow-lg px-8">
                      {sortedGallery.name}
                    </h1>
                    <button
                      onClick={() => setUploadModalOpen(true)}
                      className="
                        w-16 h-16 bg-white/10 hover:bg-white/20 text-white/60 hover:text-white
                        rounded-full shadow-lg
                        transition-all duration-200 flex items-center justify-center active:scale-95
                      "
                      aria-label="Legg til første samling"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </button>
                    <p className="text-white/30 text-sm">Legg til bilder og videoer</p>
                  </div>
                </div>
              ) : (() => {
                  const totalItems = sortedGallery.collections.reduce((sum, c) => sum + c.items.length, 0)
                  const useOverlay = totalItems < 9
                  const singleItem = totalItems === 1
                  const grid = (
                    <CollectionGrid
                      gallery={sortedGallery}
                      onItemClick={handleItemClick}
                      editMode={editMode}
                      onDeleteItem={handleDeleteItem}
                      onDeleteCollection={handleDeleteCollection}
                      deletingItemIds={deletingItemIds}
                      signedUrls={signedUrls}
                    />
                  )

                  const titleBar = (extraClass = '') => (
                    <div className={`sticky top-0 z-10 pt-4 pb-2 ${extraClass}`}>
                      <div className="relative flex items-center justify-center px-4">
                        <div className="absolute left-4 flex items-center">
                          <button
                            onClick={() => setUploadModalOpen(true)}
                            className="flex-none text-white/40 hover:text-white/80 transition-colors duration-200 focus:outline-none p-2"
                            aria-label="Last opp bilder"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </button>
                        </div>
                        <h1 className={`text-white/60 hover:text-white/90 transition-colors duration-200 font-thin tracking-tight select-none leading-none text-center drop-shadow-lg whitespace-nowrap ${sortedGallery.name.length > 32 ? 'text-lg' : sortedGallery.name.length > 20 ? 'text-xl' : 'text-3xl'}`}>
                          {sortedGallery.name}
                        </h1>
                        <div className="absolute right-4 flex items-center gap-1">
                          {editMode && (
                            <button
                              onClick={() => setEditMode(false)}
                              className="flex-none px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white text-sm font-medium transition-colors duration-200 focus:outline-none"
                            >
                              Ferdig
                            </button>
                          )}
                          {authenticated !== null && (
                            <TitleBarMenu
                              authenticated={authenticated}
                              isOwner={isOwner}
                              isEmpty={isEmpty}
                              editMode={editMode}
                              onEditToggle={() => setEditMode((prev) => !prev)}
                              onEditGallery={() => setEditGalleryOpen(true)}
                              onLogout={handleLogout}
                              loginHref={`/login?next=${encodeURIComponent(location.pathname)}`}
                              iconClass={iconClass}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )

                  if (singleItem) {
                    return (
                      <div className="relative h-dvh w-full overflow-hidden bg-gray-950">
                        <div className="absolute inset-0">{grid}</div>
                        <div className="absolute top-0 left-0 right-0 z-10">{titleBar('')}</div>
                      </div>
                    )
                  }

                  return useOverlay ? (
                    <div className="relative min-h-dvh bg-gray-950 flex flex-col">
                      {titleBar('bg-gray-950')}
                      <div className="flex-1 flex items-center">
                        <div className="w-full">{grid}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-950">
                      {titleBar('bg-gray-950')}
                      {grid}
                    </div>
                  )
                })()
              }
            </>
          ) : null}

          <PhotoViewer
            items={allItems}
            startIndex={viewerStartIndex}
            isOpen={viewerOpen}
            onClose={() => setViewerOpen(false)}
            signedUrls={signedUrls}
          />

          <UploadModal
            isOpen={uploadModalOpen}
            onClose={() => setUploadModalOpen(false)}
            onUpload={handleUpload}
            showDatePicker={true}
          />

          {editGalleryOpen && (
            <CreateGalleryModal onClose={() => setEditGalleryOpen(false)}>
              <GalleryEditForm
                galleryId={galleryId!}
                onDone={() => {
                  setEditGalleryOpen(false)
                  // Re-fetch to reflect name/cover changes
                  fetchGalleryInfo(galleryId!)
                    .then(({ name, landingImageUrl: imageUrl, landingImageBlur: blur }) => {
                      setGalleryName(name)
                      setCoverBlur(blur ?? '')
                      if (imageUrl) {
                        setCoverSrc(getCoverPublicUrl(imageUrl))
                      }
                    })
                  // Re-load full gallery data (name appears in title bar)
                  if (isOwner) {
                    fetchGallery(galleryId!).then(setGallery).catch(() => {})
                  }
                }}
                onClose={() => setEditGalleryOpen(false)}
              />
            </CreateGalleryModal>
          )}
        </>
      )}

      {/* Lock screen overlay — stays in DOM during open transition, fades out when gallery data is ready */}
      {(phase !== 'open' || showSplash || isUnlocking) && (
        <div className={`fixed inset-0 z-50 bg-gray-950 transition-opacity duration-500 ${phase === 'open' && !showSplash && !isUnlocking ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
          {/* Blur placeholder — shown immediately */}
          {coverBlur && (
            <img
              src={coverBlur}
              alt=""
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${coverReady ? 'opacity-0' : 'opacity-100'}`}
              style={{ filter: 'blur(8px)', transform: 'scale(1.05)' }}
            />
          )}

          {/* Full cover — loaded in background */}
          {coverSrc && (
            <AuthImage
              src={coverSrc}
              alt={galleryName}
              className={`absolute inset-0 w-full h-full transition-opacity duration-700 ${coverReady ? 'opacity-100' : 'opacity-0'}`}
              loading="eager"
              skeleton={false}
              elementtiming="gallery-cover"
              waitForDecode
              onLoad={() => setCoverReady(true)}
            />
          )}

          <div className={`absolute inset-0 bg-black/50 transition-opacity duration-700 ${coverReady ? 'opacity-100' : 'opacity-0'}`} />

          <button
            onClick={() => {
              if (coverReady && !isUnlocking && !isVerifying && !showSplash && authChecked) {
                handleUnlock()
              }
            }}
            aria-disabled={!coverReady || isUnlocking || isVerifying || showSplash || !authChecked}
            aria-label="Åpne"
            data-testid="lock-button"
            className="relative z-10 flex flex-col items-center justify-center h-full w-full gap-6 cursor-pointer focus:outline-none"
          >
            <h1 className="text-white text-4xl font-thin tracking-tight leading-none select-none text-center px-8 pointer-events-none">
              {galleryName}
            </h1>

            <div className="flex flex-col items-center gap-6">
              <div className={`w-16 h-16 flex items-center justify-center text-white rounded-full hover:text-gray-200 transition-opacity duration-700 ${coverReady ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <QrFinderEyeLogo
                  size={40}
                  className="text-white"
                  animating={isVerifying || isUnlocking}
                />
              </div>

              <p
                data-testid="geo-status"
                className={`text-sm text-center px-8 whitespace-pre-line max-w-xs min-h-[2.5rem] pointer-events-none ${geoError ? 'text-amber-300' : 'text-white/70'}`}
              >
                {geoError ?? ''}
              </p>
            </div>
          </button>
        </div>
      )}
    </div>
  )
}
